import { Pencil, Trash2, Copy } from 'lucide-react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { permissions } from '../../utils/permissions-utils';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import { createChecklistInstance } from './checklistApi';
import './ChecklistTemplateCard.scss';

const ChecklistTemplateCard = ({
  template,
  onDelete,
  onAccess,
}) => {
  const { name, _id: templateId, department, hospital, isCloneable } = template;
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const handleNewPatient = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // Update LRU when creating a new instance
      if (onAccess) {
        onAccess(templateId);
      }
      const newInstance = await createChecklistInstance(templateId);
      navigate(`/checklists/instance/${newInstance._id}`);
    } catch (error) {
      console.error('Failed to create checklist instance', error);
      // Optionally, show an error message to the user
    }
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) onDelete(template);
  };

  const handleCardClick = () => {
    // Update LRU when viewing template
    if (onAccess) {
      onAccess(templateId);
    }
    // Navigate to read-only view
    navigate(`/checklists/templates/view/${templateId}`);
  };

  // Check permissions based on department
  const canEdit = permissions.canEdit(user, { department: department?._id || department });
  const canDelete = permissions.canEdit(user, { department: department?._id || department });

  const hospitalName = hospital?.nameHebrew || hospital?.nameEnglish || '';
  const departmentName = department?.nameHebrew || department?.nameEnglish || '';

  return (
    <div className="checklist-template-card" dir="rtl" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div className="checklist-template-card-header">
        <h3 className="checklist-template-card-title">{name}</h3>
        {(hospitalName || departmentName) && (
          <div className="checklist-template-card-meta">
            {hospitalName && <span className="meta-item">{hospitalName}</span>}
            {departmentName && <span className="meta-item">{departmentName}</span>}
          </div>
        )}
      </div>

      <div className="checklist-template-card-actions">
        {isCloneable && (
          <button
            className="checklist-template-card-action new-patient"
            onClick={handleNewPatient}
            title="צור מופע חדש למטופל"
          >
            <Copy size={16} />
            מטופל חדש
          </button>
        )}
        <div className="admin-actions">
            {canEdit && (
                <Link
                    to={`/checklists/templates/edit/${templateId}`}
                    className="checklist-template-card-action edit"
                    aria-label="ערוך תבנית"
                    title="ערוך תבנית"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Pencil size={16} />
                </Link>
            )}
            {canDelete && (
                <button
                    className="checklist-template-card-action delete"
                    onClick={handleDeleteClick}
                    aria-label="מחק תבנית"
                    title="מחק תבנית"
                >
                    <Trash2 size={16} />
                </button>
            )}
        </div>
      </div>
    </div>
  );
};

ChecklistTemplateCard.propTypes = {
    template: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        department: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                _id: PropTypes.string,
                nameHebrew: PropTypes.string,
                nameEnglish: PropTypes.string,
            }),
        ]),
        hospital: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                _id: PropTypes.string,
                nameHebrew: PropTypes.string,
                nameEnglish: PropTypes.string,
            }),
        ]),
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
    onAccess: PropTypes.func,
};

export default ChecklistTemplateCard;